import { useReducedMotion } from "@src/packages/moon-ui/hooks/useReducedMotion";
import { useState, useRef, useEffect, useCallback } from "react";
import {
  View,
  Text,
  TextInput,
  Animated,
  Easing,
  Dimensions,
  ScrollView,
} from "react-native";
import {
  Heart,
  Users,
  ArrowRight,
  ArrowLeft,
  Send,
  Lock,
  ClipboardList,
  FlaskConical,
  Pill,
  CalendarDays,
} from "lucide-react-native";
import { isValidPhoneNumber } from "libphonenumber-js";
import { ConfettiBurst } from "@louise/moon-ui/primitives/ConfettiBurst";
import {
  Dialog,
  DialogActions,
  DialogHeader,
  PrimaryButton,
  MoonPhoneField,
  MoonToggle,
  SecondaryButton,
  TextActionButton,
  colors,
  ActionBanner,
} from "@louise/moon-ui";
import { EMBER } from "../../../../onboarding/theme";
import { useUnlinkPartner } from "@src/packages/patients/hooks/usePartnerInvite";
import type { SharingCategory } from "@src/packages/patients/hooks/usePatientSharing";
import { useTranslations } from "@src/packages/i18n";

type Props = {
  visible: boolean;
  name: string;
  phone: string;
  onChangeName: (text: string) => void;
  onChangePhone: (text: string) => void;
  onClose: () => void;
  onSend: (
    name: string,
    phone: string,
    sharingCategories: SharingCategory[],
  ) => void;
  isSending?: boolean;
  /** True once the invite mutation actually succeeded — gates the celebration. */
  sent?: boolean;
  /** True when the current pending invitation is being replaced. */
  isEditing?: boolean;
  /** Existing API-backed categories used to prefill an invitation edit. */
  initialSharingCategories?: SharingCategory[];
  scopeReady: boolean;
  error?: string | null;
  onDismissError?: () => void;
};

type InviteSharingCategory = Extract<
  SharingCategory,
  "cycles" | "appointments" | "medications" | "test_results"
>;

type InviteSharingPermissions = Record<InviteSharingCategory, boolean>;

type InviteSharingOption = {
  category: InviteSharingCategory;
  labelKey: string;
  descriptionKey: string;
  Icon: typeof CalendarDays;
};

const DEFAULT_INVITE_SHARING_PERMISSIONS: InviteSharingPermissions = {
  cycles: true,
  appointments: true,
  medications: true,
  test_results: false,
};

const INVITE_SHARING_OPTIONS: InviteSharingOption[] = [
  {
    category: "cycles",
    labelKey: "shared_cycle_data_label",
    descriptionKey: "shared_cycle_data_description",
    Icon: CalendarDays,
  },
  {
    category: "appointments",
    labelKey: "shared_appointments_label",
    descriptionKey: "shared_appointments_description",
    Icon: ClipboardList,
  },
  {
    category: "medications",
    labelKey: "shared_medications_label",
    descriptionKey: "shared_medications_description",
    Icon: Pill,
  },
  {
    category: "test_results",
    labelKey: "shared_test_results_label",
    descriptionKey: "shared_test_results_description",
    Icon: FlaskConical,
  },
];

const INVITE_SHARING_CATEGORIES: InviteSharingCategory[] = [
  "cycles",
  "appointments",
  "medications",
  "test_results",
];

function permissionsFromCategories(
  categories: SharingCategory[] | undefined,
): InviteSharingPermissions {
  return {
    cycles: categories?.includes("cycles") ?? true,
    appointments: categories?.includes("appointments") ?? true,
    medications: categories?.includes("medications") ?? true,
    test_results: categories?.includes("test_results") ?? false,
  };
}

// ── Venn diagram — large, animated, glowing ──

function PartnerVisual({ step, name }: { step: number; name: string }) {
  const t = useTranslations();
  const mergeAnim = useRef(new Animated.Value(0)).current;
  const glowAnim = useRef(new Animated.Value(0)).current;
  const pulseAnim = useRef(new Animated.Value(1)).current;

  const reducedMotion = useReducedMotion();

  useEffect(() => {
    // Progressive merge: step 0 = apart, step 1 = closer, step 2 = overlapping, step 3 = merged with glow
    const mergeTarget =
      step === 0 ? 0 : step === 1 ? 0.3 : step === 2 ? 0.7 : 1;
    Animated.spring(mergeAnim, {
      toValue: mergeTarget,
      tension: 40,
      friction: 8,
      useNativeDriver: true,
    }).start();

    // Glow on sent
    if (step === 3) {
      Animated.sequence([
        Animated.timing(glowAnim, {
          toValue: 1,
          duration: 600,
          easing: Easing.out(Easing.cubic),
          useNativeDriver: true,
        }),
        Animated.timing(glowAnim, {
          toValue: 0.5,
          duration: 1200,
          useNativeDriver: true,
        }),
      ]).start();
    }
  }, [step, mergeAnim, glowAnim]);

  useEffect(() => {
    if (reducedMotion || step !== 3) return;
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(pulseAnim, {
          toValue: 1.04,
          duration: 2000,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: true,
        }),
        Animated.timing(pulseAnim, {
          toValue: 1,
          duration: 2000,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: true,
        }),
      ]),
    );
    loop.start();
    return () => loop.stop();
  }, [step, pulseAnim, reducedMotion]);

  const size = step >= 3 ? 80 : 70;
  const isMerged = step >= 3;

  return (
    <Animated.View
      style={{
        alignItems: "center",
        justifyContent: "center",
        height: size + 40,
        marginBottom: step >= 3 ? 12 : 16,
        transform: [{ scale: pulseAnim }],
      }}
    >
      {/* Glow ring behind circles on sent step */}
      {step >= 3 && (
        <Animated.View
          style={{
            position: "absolute",
            width: size + 50,
            height: size + 50,
            borderRadius: (size + 50) / 2,
            backgroundColor: "rgba(184, 74, 22, 0.08)",
            opacity: glowAnim,
          }}
        />
      )}

      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {/* You circle */}
        <Animated.View
          style={{
            width: size,
            height: size,
            borderRadius: size / 2,
            backgroundColor: isMerged
              ? "rgba(184, 74, 22, 0.1)"
              : "rgba(184, 74, 22, 0.06)",
            borderWidth: isMerged ? 2 : 1.5,
            borderColor: isMerged
              ? "rgba(184, 74, 22, 0.3)"
              : "rgba(184, 74, 22, 0.2)",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1,
            transform: [
              {
                translateX: mergeAnim.interpolate({
                  inputRange: [0, 1],
                  outputRange: [0, 12],
                }),
              },
            ],
          }}
        >
          <Heart
            size={isMerged ? 20 : 18}
            color={EMBER.colors.warm}
            strokeWidth={1.5}
            fill={isMerged ? "rgba(184, 74, 22, 0.15)" : "none"}
          />
          {!isMerged && (
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 9,
                fontWeight: "500",
                color: EMBER.colors.textFaint,
                marginTop: 2,
              }}
            >
              {t("you.modals.partner_invite.visual_you")}
            </Text>
          )}
        </Animated.View>

        {/* Partner circle */}
        <Animated.View
          style={{
            width: size,
            height: size,
            borderRadius: size / 2,
            backgroundColor: isMerged
              ? "rgba(184, 74, 22, 0.1)"
              : step >= 1
                ? "rgba(184, 74, 22, 0.04)"
                : "rgba(255, 255, 255, 0.5)",
            borderWidth: isMerged ? 2 : 1.5,
            borderColor: isMerged
              ? "rgba(184, 74, 22, 0.3)"
              : step >= 1
                ? "rgba(184, 74, 22, 0.15)"
                : "rgba(184, 74, 22, 0.1)",
            borderStyle: step >= 1 ? "solid" : "dashed",
            alignItems: "center",
            justifyContent: "center",
            transform: [
              {
                translateX: mergeAnim.interpolate({
                  inputRange: [0, 1],
                  outputRange: [-8, -12],
                }),
              },
            ],
          }}
        >
          <Users
            size={isMerged ? 20 : 18}
            color={step >= 1 ? EMBER.colors.warm : EMBER.colors.textFaint}
            strokeWidth={1.5}
          />
          {!isMerged && name.trim() ? (
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 9,
                fontWeight: "500",
                color:
                  step >= 1 ? EMBER.colors.textLight : EMBER.colors.textFaint,
                marginTop: 2,
              }}
              numberOfLines={1}
            >
              {name.trim()}
            </Text>
          ) : (
            !isMerged && (
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 9,
                  fontWeight: "500",
                  color: EMBER.colors.textFaint,
                  marginTop: 2,
                }}
              >
                ?
              </Text>
            )
          )}
        </Animated.View>
      </View>

      {/* "Together" label on merged state */}
      {isMerged && (
        <Animated.Text
          className="font-dm-sans"
          style={{
            fontSize: 11,
            fontWeight: "600",
            color: EMBER.colors.warm,
            letterSpacing: 0.5,
            textTransform: "uppercase",
            marginTop: 8,
            opacity: glowAnim,
          }}
        >
          {t("you.modals.partner_invite.visual_together")}
        </Animated.Text>
      )}
    </Animated.View>
  );
}

// ── Step indicator ──

function StepDots({ current, total }: { current: number; total: number }) {
  const t = useTranslations();

  return (
    <View
      accessible
      accessibilityRole="progressbar"
      accessibilityLabel={t("you.modals.partner_invite.step_progress", {
        params: { current: String(current + 1), total: String(total) },
      })}
      style={{
        flexDirection: "row",
        justifyContent: "center",
        gap: 6,
        marginBottom: 20,
      }}
    >
      {Array.from({ length: total }).map((_, i) => (
        <View
          key={i}
          style={{
            width: i === current ? 18 : 6,
            height: 6,
            borderRadius: 3,
            backgroundColor:
              i === current
                ? EMBER.colors.warm
                : i < current
                  ? "rgba(184, 74, 22, 0.35)"
                  : "rgba(0, 0, 0, 0.06)",
          }}
        />
      ))}
    </View>
  );
}

// ── Step 0: Who is your person ──

function StepWho({
  name,
  onChangeName,
  onNext,
}: {
  name: string;
  onChangeName: (t: string) => void;
  onNext: () => void;
}) {
  const t = useTranslations();
  const inputRef = useRef<TextInput>(null);

  useEffect(() => {
    const timeout = setTimeout(() => inputRef.current?.focus(), 400);
    return () => clearTimeout(timeout);
  }, []);

  return (
    <View>
      <DialogHeader
        align="center"
        title={t("you.modals.partner_invite.step_who_title")}
        subtitle={t("you.modals.partner_invite.step_who_subtitle")}
        style={{ marginBottom: 28 }}
      />

      <TextInput
        ref={inputRef}
        value={name}
        onChangeText={onChangeName}
        placeholder={t("you.modals.partner_invite.step_who_name_placeholder")}
        placeholderTextColor={EMBER.colors.textFaint}
        autoCapitalize="words"
        autoCorrect={false}
        returnKeyType="next"
        onSubmitEditing={() => name.trim() && onNext()}
        accessibilityLabel={t("you.modals.partner_invite.step_who_name_label")}
        accessibilityHint={t("you.modals.partner_invite.step_who_name_hint")}
        className="font-dm-sans"
        style={{
          width: "100%",
          paddingVertical: 16,
          paddingHorizontal: 20,
          borderRadius: 14,
          borderWidth: 1,
          borderColor: name.trim()
            ? "rgba(184, 74, 22, 0.25)"
            : EMBER.colors.border,
          backgroundColor: name.trim()
            ? "rgba(184, 74, 22, 0.02)"
            : "rgba(255, 255, 255, 0.6)",
          fontSize: 17,
          color: EMBER.colors.text,
          textAlign: "center",
        }}
      />
    </View>
  );
}

// ── Step 1: Sharing permissions ──

function StepShare({
  name,
  permissions,
  onToggle,
  scopeReady,
}: {
  name: string;
  permissions: InviteSharingPermissions;
  onToggle: (category: InviteSharingCategory) => void;
  scopeReady: boolean;
}) {
  const t = useTranslations();

  return (
    <View>
      <DialogHeader
        align="center"
        title={t("you.modals.partner_invite.step_share_title", {
          params: { name },
        })}
        subtitle={t("you.modals.partner_invite.step_share_subtitle")}
        style={{ marginBottom: 20 }}
      />

      <View>
        {INVITE_SHARING_OPTIONS.map(
          ({ category, labelKey, descriptionKey, Icon }, index) => {
            const label = t(`you.modals.partner_invite.${labelKey}`);
            const description = t(
              `you.modals.partner_invite.${descriptionKey}`,
            );

            return (
              <View
                key={category}
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  paddingVertical: 11,
                  borderTopWidth: index > 0 ? 1 : 0,
                  borderTopColor: "rgba(0, 0, 0, 0.06)",
                  gap: 12,
                }}
              >
                <Icon size={18} color={EMBER.colors.textMid} strokeWidth={2} />
                <View style={{ flex: 1 }}>
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 14,
                      fontWeight: "500",
                      color: EMBER.colors.text,
                    }}
                  >
                    {label}
                  </Text>
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 11,
                      color: EMBER.colors.textFaint,
                      marginTop: 1,
                    }}
                  >
                    {description}
                  </Text>
                </View>
                <MoonToggle
                  value={permissions[category]}
                  onChange={() => onToggle(category)}
                  accessibilityLabel={label}
                />
              </View>
            );
          },
        )}
      </View>

      {!scopeReady && (
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 12,
            color: EMBER.colors.textFaint,
            textAlign: "center",
            lineHeight: 17,
            marginTop: 12,
          }}
        >
          {t("you.modals.partner_invite.sharing_scope_required")}
        </Text>
      )}

      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "center",
          gap: 6,
          marginTop: 14,
        }}
      >
        <Lock size={11} color={EMBER.colors.textFaint} strokeWidth={1.5} />
        <Text
          className="font-dm-sans"
          style={{ fontSize: 11, color: EMBER.colors.textFaint }}
        >
          {t("you.modals.partner_invite.step_share_note")}
        </Text>
      </View>
    </View>
  );
}

// ── Step 2: Phone ──

function StepPhone({
  name,
  phone,
  onChangePhone,
}: {
  name: string;
  phone: string;
  onChangePhone: (t: string) => void;
}) {
  const t = useTranslations();

  return (
    <View>
      <DialogHeader
        align="center"
        title={t("you.modals.partner_invite.step_phone_title", {
          params: { name },
        })}
        subtitle={t("you.modals.partner_invite.step_phone_subtitle")}
        style={{ marginBottom: 24 }}
      />

      <MoonPhoneField
        label={t("you.modals.partner_invite.step_phone_number_label")}
        value={phone}
        onChange={onChangePhone}
        placeholder={t(
          "you.modals.partner_invite.step_phone_number_placeholder",
        )}
      />
    </View>
  );
}

// ── Step 3: Celebration ──

function StepSent({
  name,
  onClose,
  isSending,
  isEditing,
}: {
  name: string;
  onClose: () => void;
  isSending?: boolean;
  isEditing?: boolean;
}) {
  const t = useTranslations();
  const fadeIn = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    Animated.timing(fadeIn, {
      toValue: 1,
      duration: 500,
      delay: 300,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: true,
    }).start();
  }, [fadeIn]);

  return (
    <Animated.View
      style={{
        alignItems: "center",
        opacity: fadeIn,
        transform: [
          {
            translateY: fadeIn.interpolate({
              inputRange: [0, 1],
              outputRange: [10, 0],
            }),
          },
        ],
      }}
    >
      <DialogHeader
        align="center"
        title={t(
          isEditing
            ? "you.modals.partner_invite.step_updated_title"
            : "you.modals.partner_invite.step_sent_title",
        )}
        subtitle={t(
          isEditing
            ? "you.modals.partner_invite.step_updated_message"
            : "you.modals.partner_invite.step_sent_message",
          { params: { name } },
        )}
        style={{ marginBottom: 28 }}
        subtitleStyle={{ paddingHorizontal: 8 }}
      />

      <DialogActions style={{ marginTop: 4 }}>
        <PrimaryButton
          label={t("you.modals.partner_invite.done")}
          onPress={onClose}
          disabled={isSending}
          loading={isSending}
        />
      </DialogActions>
    </Animated.View>
  );
}

// ── Main modal ──

export default function PartnerInviteModal({
  visible,
  name,
  phone,
  onChangeName,
  onChangePhone,
  onClose,
  onSend,
  isSending,
  sent,
  isEditing,
  initialSharingCategories,
  scopeReady,
  error,
  onDismissError,
}: Props) {
  const t = useTranslations();
  const [step, setStep] = useState(0);
  const [permissions, setPermissions] = useState(
    DEFAULT_INVITE_SHARING_PERMISSIONS,
  );
  const fadeAnim = useRef(new Animated.Value(1)).current;
  const [confettiTrigger, setConfettiTrigger] = useState(0);
  const { height: windowHeight } = Dimensions.get("window");
  const dialogMaxHeight = windowHeight * 0.85;

  // Detect the backend "already linked" failure so we can offer a recovery
  // path without forcing the user to find the (currently hidden) manage
  // modal.
  const unlinkMutation = useUnlinkPartner();
  const alreadyLinked = !!error && /already.*linked partner/i.test(error);
  // The API's error text is for the logs; she reads translated copy. The
  // already-linked case still keys off that text, the only signal the API gives.
  const errorMessage = error
    ? alreadyLinked
      ? t("you.modals.partner_invite.already_have_linked_partner")
      : t("you.modals.partner_invite.could_not_send_invite")
    : null;
  const handleUnlinkAndRetry = async () => {
    try {
      await unlinkMutation.mutateAsync();
      onDismissError?.();
    } catch {
      // mutation error state is rendered inline below
    }
  };

  // Keep the latest reset callback in a ref so the open-reset effect does
  // not re-fire when parents pass inline arrows.
  const resetInviteStateRef = useRef(onDismissError);
  useEffect(() => {
    resetInviteStateRef.current = onDismissError;
  }, [onDismissError]);

  // Reset the flow whenever the invite dialog opens.
  useEffect(() => {
    if (visible) {
      setStep(0);
      setPermissions(permissionsFromCategories(initialSharingCategories));
      setConfettiTrigger(0);
      // Clear any previous error/success so a stale isSuccess from an
      // earlier invite cannot auto-advance this fresh flow (#1378).
      resetInviteStateRef.current?.();
    }
  }, [initialSharingCategories, visible]);

  const animateStep = useCallback(
    (next: number) => {
      Animated.timing(fadeAnim, {
        toValue: 0,
        duration: 100,
        useNativeDriver: true,
      }).start(() => {
        setStep(next);
        Animated.timing(fadeAnim, {
          toValue: 1,
          duration: 250,
          easing: Easing.out(Easing.cubic),
          useNativeDriver: true,
        }).start();
      });
    },
    [fadeAnim],
  );

  const handleNext = () => animateStep(step + 1);
  const handleBack = () => animateStep(step - 1);

  const handleTogglePermission = (category: InviteSharingCategory) => {
    setPermissions((current) => ({
      ...current,
      [category]: !current[category],
    }));
  };

  const handleSend = () => {
    if (isSending) return;
    const sharingCategories: SharingCategory[] = [
      ...INVITE_SHARING_CATEGORIES.filter((category) => permissions[category]),
    ];

    // MoonPhoneField already emits a validated canonical E.164 number. The
    // celebration step waits for the mutation to actually succeed; failures
    // keep the user on the phone step with the inline error and recovery
    // actions.
    onSend(name.trim(), phone, sharingCategories);
  };

  // Advance to the "sent" celebration only on real success, and only from
  // the phone step so a stale success from a previous open can't skip
  // the flow (the entrance effect also resets the mutation below).
  useEffect(() => {
    if (visible && sent && step === 2) {
      setConfettiTrigger((c) => c + 1);
      animateStep(3);
    }
  }, [visible, sent, step, animateStep]);

  const handleDone = () => {
    onClose();
  };

  const canAdvance =
    (step === 0 && name.trim().length > 0) ||
    step === 1 ||
    (step === 2 && isValidPhoneNumber(phone));
  const canClose = !isSending;

  return (
    <Dialog
      containerStyle={{ maxHeight: dialogMaxHeight }}
      visible={visible}
      onClose={canClose ? onClose : () => {}}
      layout="centered"
      animationType="fade"
      closeButton={canClose ? "minimal" : false}
      dismissOnBackdropTap={canClose}
      surfaceStyle={{ maxHeight: dialogMaxHeight }}
      contentStyle={{
        paddingTop: 0,
        paddingHorizontal: 0,
        paddingBottom: 0,
        maxHeight: dialogMaxHeight,
      }}
    >
      {/* Confetti burst */}
      <ConfettiBurst trigger={confettiTrigger} />

      {/* Error banner */}
      {error && (
        <View style={{ marginHorizontal: 20, marginTop: 16, gap: 10 }}>
          <ActionBanner
            variant="important"
            message={errorMessage ?? ""}
            onDismiss={onDismissError}
            dismissLabel={t("you.modals.partner_invite.dismiss_error")}
            dismissAction="you.modals.partner_invite.dismiss_error"
          />
          {alreadyLinked && (
            <TextActionButton
              label={
                unlinkMutation.isPending
                  ? t("you.modals.partner_invite.unlink_pending")
                  : t("you.modals.partner_invite.unlink_action")
              }
              action="you.modals.partner_invite.unlink_action"
              tone="destructive"
              size="sm"
              onPress={handleUnlinkAndRetry}
              disabled={unlinkMutation.isPending}
              style={{ alignSelf: "flex-start" }}
            />
          )}
        </View>
      )}

      <ScrollView
        contentContainerStyle={{
          paddingHorizontal: 24,
          paddingTop: 8,
          paddingBottom: 24,
        }}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
        bounces={false}
        style={{ flexShrink: 1 }}
      >
        {/* Visual — large Venn diagram */}
        <PartnerVisual step={step} name={name} />

        {/* Step dots — keep the completed fourth step visible in context. */}
        <StepDots current={step} total={4} />

        {/* Step content with crossfade */}
        <Animated.View style={{ opacity: fadeAnim }}>
          {step === 0 && (
            <StepWho
              name={name}
              onChangeName={onChangeName}
              onNext={handleNext}
            />
          )}
          {step === 1 && (
            <StepShare
              name={name.trim()}
              permissions={permissions}
              onToggle={handleTogglePermission}
              scopeReady={scopeReady}
            />
          )}
          {step === 2 && (
            <StepPhone
              name={name.trim()}
              phone={phone}
              onChangePhone={onChangePhone}
            />
          )}
          {step === 3 && (
            <StepSent
              name={name.trim()}
              onClose={handleDone}
              isSending={isSending}
              isEditing={isEditing}
            />
          )}
        </Animated.View>

        {/* Navigation — steps 0 through 2 only */}
        {step < 3 && (
          <DialogActions style={{ marginTop: 24 }}>
            {step > 0 && (
              <SecondaryButton
                label={t("you.modals.partner_invite.back")}
                onPress={handleBack}
                leading={
                  <ArrowLeft
                    size={14}
                    color={EMBER.colors.textLight}
                    strokeWidth={1.5}
                  />
                }
                flex={1}
              />
            )}
            <View
              testID={
                step === 2 ? "partner-invite-send" : "partner-invite-continue"
              }
              style={step > 0 ? { flex: 1 } : undefined}
            >
              {step === 2 ? (
                <PrimaryButton
                  label={t(
                    isEditing
                      ? "you.modals.partner_invite.update_invite"
                      : "you.modals.partner_invite.send_invite",
                    { params: { name: name.trim() } },
                  )}
                  onPress={handleSend}
                  disabled={!canAdvance || isSending || !scopeReady}
                  loading={isSending}
                  contentStyle={{ paddingHorizontal: 12 }}
                  leading={
                    <Send size={15} color={colors.surface} strokeWidth={1.5} />
                  }
                />
              ) : (
                <PrimaryButton
                  label={t("you.modals.partner_invite.continue")}
                  onPress={handleNext}
                  disabled={!canAdvance}
                  trailing={
                    <ArrowRight
                      size={14}
                      color={colors.surface}
                      strokeWidth={2}
                    />
                  }
                />
              )}
            </View>
          </DialogActions>
        )}
      </ScrollView>
    </Dialog>
  );
}
